<script setup lang="ts">
import FillLockIcon from '@/components/common/icons/FillLockIcon.vue'
import { isMobile } from '@/util/helper'
import { BookOpen01Icon } from '@gohighlevel/ghl-icons/24/outline'
import { UISkeleton } from '@gohighlevel/ghl-ui'
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

interface Position {
  left: number
  top: number
}

interface ParentRect {
  left: number
  right: number
  top: number
  height: number
  width: number
  parentElementRect: DOMRect
}

interface TooltipState {
  type: 'loading' | 'error' | 'success' | null
  message?: string
  courseTitle?: string
}
const show = ref(false)
const position = ref<Position>({ left: 0, top: 0 })
const parentRect = ref<ParentRect | null>(null)
const tooltipState = ref<TooltipState>({ type: null })
const { t } = useI18n()

function handleShowLoadingTooltip(event: CustomEvent) {
  position.value.left = event?.detail?.parent?.left
  position.value.top = event?.detail?.parent?.top
  parentRect.value = event?.detail?.parent
  tooltipState.value = {
    type: 'loading',
  }
  show.value = true
}

function handleShowErrorTooltip(event: CustomEvent) {
  position.value.left = event?.detail?.parent?.left
  position.value.top = event?.detail?.parent?.top
  parentRect.value = event?.detail?.parent
  tooltipState.value = {
    type: 'error',
    message: event?.detail?.errorMessage,
  }
}

function handleHideTooltip() {
  if (show.value) {
    show.value = false
    tooltipState.value = { type: null }
  }
}

function handleShowSuccessTooltip(event: CustomEvent) {
  position.value.left = event?.detail?.parent?.left
  position.value.top = event?.detail?.parent?.top
  parentRect.value = event?.detail?.parent
  tooltipState.value = {
    type: 'success',
    courseTitle: event?.detail?.courseName,
  }
}

onMounted(() => {
  const tooltipElement = document.getElementById('course-tooltip')
  if (tooltipElement) {
    tooltipElement.addEventListener(
      'show-course-loading-tooltip',
      handleShowLoadingTooltip as EventListener
    )
    tooltipElement.addEventListener(
      'show-course-error-tooltip',
      handleShowErrorTooltip as EventListener
    )
    tooltipElement.addEventListener(
      'show-course-success-tooltip',
      handleShowSuccessTooltip as EventListener
    )
    tooltipElement.addEventListener('close-course-tooltip', handleHideTooltip)
  }
  // Add scroll event listener to hide tooltip when scrolling
  window.addEventListener('scroll', handleHideTooltip)
  window.addEventListener('wheel', handleHideTooltip)
})

onUnmounted(() => {
  const tooltipElement = document.getElementById('event-tooltip')
  tooltipElement?.removeEventListener(
    'show-course-loading-tooltip',
    handleShowLoadingTooltip as EventListener
  )
  tooltipElement?.removeEventListener(
    'show-course-error-tooltip',
    handleShowErrorTooltip as EventListener
  )
  tooltipElement?.removeEventListener('close-course-tooltip', handleHideTooltip)
  window.removeEventListener('scroll', handleHideTooltip)
})

watch(tooltipState, () => {
  if (!show.value || !parentRect.value) return

  nextTick(() => {
    const tooltipElement = document.getElementById('course-tooltip')
    if (!tooltipElement) return

    const tooltipHeight = tooltipElement.getBoundingClientRect().height
    const spaceAbove = parentRect.value!.top
    const toolTipOffset = 8 // Reduced from 30px for better positioning

    // Check if there's enough space above the target element
    if (spaceAbove > tooltipHeight + toolTipOffset) {
      // Position above the target
      position.value.top = parentRect.value!.top - tooltipHeight - toolTipOffset
    } else {
      // Position below the target
      position.value.top =
        parentRect.value!.top + parentRect.value!.height + toolTipOffset
    }
  })
})
</script>

<template>
  <teleport to="body">
    <div
      v-show="show && !isMobile()"
      id="course-tooltip"
      role="tooltip"
      class="fixed max-w-[304px] overflow-hidden rounded-lg border border-solid border-communities-border bg-communities-background p-1 px-2 text-center shadow-lg"
      :style="{
        top: position.top + 'px',
        left: position.left + 'px',
        zIndex: 3000,
        maxWidth: tooltipState.type === 'error' ? '250px' : '300px',
      }"
    >
      <!-- Loading State -->
      <div v-if="tooltipState.type === 'loading'" class="flex flex-col gap-0">
        <!-- Course title skeleton -->
        <UISkeleton height="20px" width="150px" :sharp="false" />
      </div>
      <!-- Success State -->
      <div
        v-else-if="tooltipState.type === 'success'"
        class="flex items-center gap-[2px] text-xs font-semibold text-communities-font-primary"
      >
        <div class="mr-[2px] h-4 w-4">
          <BookOpen01Icon
            class="h-4 w-4 text-communities-font-primary"
            :fill="'var(--fontPrimary)'"
          />
        </div>
        <span class="truncate">{{ tooltipState.courseTitle }}</span>
      </div>

      <!-- Error State -->
      <div
        v-else-if="tooltipState.type === 'error'"
        class="flex items-center text-xs font-semibold text-communities-font-primary"
      >
        <FillLockIcon
          class="h-5 w-5 text-communities-font-primary"
          :fill="'var(--fontPrimary)'"
        />
        {{
          tooltipState.message === 'not-accessible'
            ? t('courses.courseRestricted')
            : t('courses.courseDeleted')
        }}
      </div>
    </div>
  </teleport>
</template>
